<template>
  <div class="p-drawer daytime-advice-sheet-sidebar">
    <!-- アイコン部 -->
    <div class="p-drawer__icons">
      <button
        type="button"
        aria-label="サイドバーを拡大"
        :class="{ 'p-drawer__icons__expand': true, 'is-disable': isSidebarWidthMax }"
        @click="expand"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </button>
      <button
        type="button"
        aria-label="サイドバーを縮小"
        :class="{ 'p-drawer__icons__shrink': true, 'is-disable': isSidebarWidthMin }"
        @click="shrink"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </button>
    </div>

    <!-- サイドバー本体部 -->
    <v-navigation-drawer
      class="p-drawer__main"
      :width="sidebarWidth"
      location="right"
      permanent
    >
      <div v-show="!isSidebarWidthMin">
        <v-tabs
          v-model="selectedTab"
          :height="40"
          bg-color="grey-lighten-3"
        >
          <v-tab
            class="ma-0"
            :height="40"
            value="userInfo"
          >
            <v-icon class="mr-1">
              mdi-magnify
            </v-icon>
            <span class="font-weight-bold">ユーザー情報</span>
          </v-tab>
          <v-tab
            class="ma-0 ml-4"
            :height="40"
            value="journal"
          >
            <v-icon class="mr-1">
              mdi-book-open-page-variant
            </v-icon>
            <span class="font-weight-bold">ジャーナル検索</span>
          </v-tab>
        </v-tabs>

        <div class="p-drawer__tab-body">
          <daytime-advice-sheet-user-info-tab
            v-show="selectedTab === 'userInfo'"
            ref="userInfoTabRef"
            :daytime-advice-sheet="daytimeAdviceSheet || {}"
            :exclude-sheet-id="route.params.id"
          />
          <journal-tab
            v-show="selectedTab === 'journal'"
            ref="journalTabRef"
            class="p-drawer__journal-tab"
            type="daytimeAdviceSheet"
          /> <!-- タブ「ジャーナル検索」 -->
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

import JournalTab from "@/vue/components/shared/sidebar/Journal/journalTab.vue";
import DaytimeAdviceSheetUserInfoTab from "./DaytimeAdviceSheetUserInfoTab.vue";

// サイドバーのデフォルトの展開幅 最小=1, 最大=3
const SIDEBAR_DEFAULT_RANK = 2;

const store = useStore();
const route = useRoute();

const journalTabRef = ref(null);
const userInfoTabRef = ref(null);
const selectedTab = ref("userInfo");

const sidebarWidthRank = computed(() => store.getters.sidebarWidthRank);
const sidebarWidth = computed(() => store.getters.sidebarWidth);
const isSidebarWidthMin = computed(() => store.getters.isSidebarWidthMin);
const isSidebarWidthMax = computed(() => store.getters.isSidebarWidthMax);
const daytimeAdviceSheet = computed(() => store.getters.daytimeAdviceSheet);
const daytimeSidebarWidthRankByUser = computed(() => store.getters.daytimeSidebarWidthRankByUser);

// 現在表示中ユーザーのIDを返す。シートがまだ未取得の場合は null
const currentUserId = computed(() => daytimeAdviceSheet.value?.userId ?? null);

// サイドバー幅を変更し、ユーザーごとの設定をストアに保存する
const updateWidthRank = (newRank) => {
  store.dispatch("updateSidebarWidthRank", newRank);
  if (currentUserId.value != null) {
    store.dispatch("saveDaytimeSidebarWidthRankForUser", { userId: currentUserId.value, rank: newRank });
  }
};

const expand = () => {
  if (!isSidebarWidthMax.value) updateWidthRank(sidebarWidthRank.value + 1);
};

const shrink = () => {
  if (!isSidebarWidthMin.value) updateWidthRank(sidebarWidthRank.value - 1);
};

// --- ▼ DaytimeAdviceSheetのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---

// 直前に初期化したシートID。同じシートの再フェッチ（保存・キャンセル・送信後など）では
// サイドバー幅・タブ選択をリセットしないために使用する。
const initializedForSheetId = ref(null);

const normalizeSheetId = (value) => {
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

const initializeTabs = () => {
  const targetId = normalizeSheetId(route.params.id);
  const sheet = daytimeAdviceSheet.value;
  const isTargetSheet = targetId != null && !!sheet && Number(sheet.id) === targetId;

  // 別シートへの遷移時のみサイドバー幅とタブ選択をリセットする
  if (initializedForSheetId.value !== targetId) {
    initializedForSheetId.value = targetId;
    selectedTab.value = "userInfo";
    // 検索タブ経由（daytimeAdviceSearchView）で開いた場合は常にデフォルト幅を使用する。
    // ルームビュー経由の場合のみユーザーごとに保存済みのランクを復元する。
    const isSearchView = route.name === "daytimeAdviceSearchView";
    const userId = (!isSearchView && isTargetSheet) ? sheet.userId : null;
    const savedRank = userId != null ? (daytimeSidebarWidthRankByUser.value[userId] ?? SIDEBAR_DEFAULT_RANK) : SIDEBAR_DEFAULT_RANK;
    store.dispatch("updateSidebarWidthRank", savedRank);
  }

  if (isTargetSheet) {
    if (journalTabRef.value) journalTabRef.value.start();
    if (userInfoTabRef.value) userInfoTabRef.value.start();
  } else {
    if (journalTabRef.value) journalTabRef.value.clear();
    if (userInfoTabRef.value) userInfoTabRef.value.clear();
  }
};

onMounted(() => {
  initializeTabs();
});
watch(
  () => daytimeAdviceSheet.value,
  () => { initializeTabs(); }
);

// --- ▲ DaytimeAdviceSheetのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---
</script>

<style lang="scss" scoped>
.p-drawer {
  button {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  &__icons {
    position: absolute;
    z-index: 2000;
    /* v-navigation-drawerになぜか動的にz-indexが指定される */
    margin-left: -12px;
    display: flex;
    flex-direction: column;
    margin-top: 50px;

    &__expand {
      transform: rotate(180deg);
      height: 25px;

      &.is-disable {
        opacity: 0;
      }
    }

    &__shrink {
      height: 25px;
      margin-top: 8px;

      &.is-disable {
        opacity: 0;
      }
    }
  }

  &__main {
    border-left: 1px solid #C9C5C5;
    /* 96px = compact TopMenu(48px) + v-tabs(48px), 64px = DaytimeAdviceActionBar(ボトムバー)の高さ */
    height: calc(100vh - 96px - 64px) !important;
    overflow: auto;
  }

  &__header {
    height: 40px;
    background-color: #eee;
    border-bottom: 1px solid #ccc;
  }

  &__tab-body {
    min-height: calc(100vh - 152px - 64px);
    background-color: #f7f7f7;
  }

  &__journal-tab {
    font-size: 14px;
  }

  :deep(.v-navigation-drawer) {
    /* absoluteがつくとflexでうまく配置されない */
    position: initial !important;
  }
}
</style>
